import { ThemeEvents } from '@theme/events';

/**
 * Tiered Offers Manager
 * Handles free shipping at $69, free t-shirt at $120, and free sweatshirt at $199
 */
class TieredOffersManager {
  constructor() {
    this.milestones = [
      { amount: 69, label: 'Free Shipping', achieved: false },
      { amount: 120, label: 'Free T-Shirt', worth: '$30', achieved: false, productHandle: 'the-signature-crew-white-copy', productId: '9026579792092' },
      { amount: 199, label: 'Free SweatShirt', worth: '$70', achieved: false, productHandle: 'unisex-garment-dyed-crewneck-sweatshirt-copy', productId: '9083460780252' }
    ];
    this.initialized = false;
    this.debugMode = false; // Set to true for debugging
    this.addedFreeItems = new Set(); // Track which free items have been added
    this.lastCartTotal = 0; // Track cart total to detect significant changes (always a number)
    
    this.init();
  }

  init() {
    if (this.initialized) {
      if (this.debugMode) {
        console.log('TieredOffers - Already initialized, skipping');
      }
      return;
    }
    
    if (this.debugMode) {
      console.log('TieredOffers - Initializing TieredOffersManager');
    }
    
    // Listen for cart updates
    document.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate.bind(this));
    // Listen for quantity changes to trigger removal checks
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.handleQuantityChange.bind(this));
    document.addEventListener('cart:updated', this.handleCartUpdate.bind(this)); // Alternative cart update event
    document.addEventListener('DOMContentLoaded', this.updateOffersDisplay.bind(this));
    
    // Listen for cart drawer opening
    document.addEventListener('cart-drawer:opened', this.handleCartDrawerOpened.bind(this));
    
    // Also listen for cart drawer content changes using mutation observer
    this.setupCartDrawerObserver();
    
    // Initial calculation - with error protection
    try {
      this.updateOffersDisplay();
    } catch (error) {
      console.error('TieredOffers - Error during initial calculation:', error);
    }
    this.initialized = true;
    
    if (this.debugMode) {
      console.log('TieredOffers - Initialization complete');
    }
  }

  async getCartTotal() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      if (this.debugMode) {
        console.log('TieredOffers - Raw cart response:', { 
          total_price: cart.total_price, 
          item_count: cart.item_count,
          items: cart.items?.length || 0 
        });
      }
      
      // Calculate the correct total by subtracting free item prices
      let correctedTotal = cart.total_price || 0;
      let freeItemsTotal = 0;
      
      if (cart.items) {
        cart.items.forEach(item => {
          if (item.properties && item.properties._free === 'true') {
            freeItemsTotal += item.final_line_price;
            if (this.debugMode) {
              console.log(`TieredOffers - Subtracting free item: ${item.product_title} - $${(item.final_line_price / 100).toFixed(2)}`);
            }
          }
        });
      }
      
      correctedTotal = correctedTotal - freeItemsTotal;
      
      if (this.debugMode && freeItemsTotal > 0) {
        console.log(`TieredOffers - Original total: $${(cart.total_price / 100).toFixed(2)}`);
        console.log(`TieredOffers - Free items total: $${(freeItemsTotal / 100).toFixed(2)}`);
        console.log(`TieredOffers - Corrected total: $${(correctedTotal / 100).toFixed(2)}`);
      }
      
      const finalTotal = correctedTotal / 100; // Convert from cents to dollars
      
      // Ensure we return a valid number
      return isNaN(finalTotal) ? 0 : Math.max(0, finalTotal);
    } catch (error) {
      console.error('TieredOffers - Error fetching cart total:', error);
      return 0;
    }
  }

  async updateOffersDisplay(providedCartTotal) {
    try {
      const rawCartTotal = providedCartTotal !== undefined ? providedCartTotal : await this.getCartTotal();
      
      // Ensure cartTotal is a valid number
      const cartTotal = isNaN(parseFloat(rawCartTotal)) ? 0 : parseFloat(rawCartTotal);
      
      if (this.debugMode) {
        console.log('TieredOffers - Cart total:', cartTotal, 'Previous total:', this.lastCartTotal);
      }
    
    // Check if cart total decreased (indicating possible item removal/quantity decrease)
    const totalDecrease = this.lastCartTotal - cartTotal;
    const significantDecrease = totalDecrease > 5; // $5 or more decrease
    
    if (significantDecrease && this.lastCartTotal > 0) {
      if (this.debugMode) {
        console.log(`TieredOffers - Cart total decrease detected: $${totalDecrease.toFixed(2)}. Checking for removal eligibility.`);
      }
      
      // Check for removal eligibility after a delay to avoid interfering with ongoing cart operations
      setTimeout(async () => {
        await this.checkAndRemoveIneligibleFreeItems();
      }, 500); // Reduced delay for faster response
    }
    
    // Update last cart total (ensure it's always a number)
    this.lastCartTotal = isNaN(cartTotal) ? 0 : cartTotal;
    
    // Update milestone achievements
    this.milestones.forEach(milestone => {
      milestone.achieved = cartTotal >= milestone.amount;
    });
    
    // Calculate progress proportionally per milestone segment so markers align with UI dividers
    const milestoneAmounts = this.milestones.map(m => m.amount); // [69, 120, 199]
    const [m1 = 69, m2 = 120, m3 = 199] = milestoneAmounts;
    const totalSegments = Math.max(1, this.milestones.length); // 3 segments
    const segmentWidth = 100 / totalSegments; // 33.333...
    let progressPercentage = 0;

    if (cartTotal <= 0) {
      progressPercentage = 0;
    } else if (cartTotal < m1) {
      // 0 -> first milestone maps to 0 -> 1 segment
      progressPercentage = (cartTotal / m1) * segmentWidth;
    } else if (cartTotal < m2) {
      // First -> second milestone maps to 1 -> 2 segments
      progressPercentage = segmentWidth + ((cartTotal - m1) / (m2 - m1)) * segmentWidth;
    } else if (cartTotal < m3) {
      // Second -> third milestone maps to 2 -> 3 segments
      progressPercentage = segmentWidth * 2 + ((cartTotal - m2) / (m3 - m2)) * segmentWidth;
    } else {
      progressPercentage = 100;
    }
    
    // Update progress bar
    const progressFillEl = document.querySelector('[data-progress-fill]');
    if (progressFillEl) {
      progressFillEl.style.setProperty('--progress-width', progressPercentage + '%');
    }
    
    // Update tooltip position and value
    const tooltipEl = document.querySelector('.gwp-bar__tooltip');
    if (tooltipEl) {
      // Cap tooltip position at 85% to prevent it from going off-screen
      const tooltipPosition = Math.min(progressPercentage, 85);
      tooltipEl.style.setProperty('--position', tooltipPosition + '%');
      const tooltipMoneyEl = tooltipEl.querySelector('.money');
      if (tooltipMoneyEl) {
        // Change tooltip text when progress is 100%
        if (progressPercentage >= 100) {
          tooltipMoneyEl.textContent = 'All Gifts Unlocked';
        } else {
          tooltipMoneyEl.textContent = `$${cartTotal.toFixed(2)}`;
        }
      }
    }
    
    // Update milestone thresholds and labels
    this.milestones.forEach((milestone, index) => {
      const thresholdEl = document.querySelector(`.gwp-bar__thersholds li[data-milestone="${milestone.amount}"]`);
      const labelEl = document.querySelector(`.gwp-bar__labels li[data-forloop-index0="${index}"]`);
      
      if (thresholdEl) {
        thresholdEl.classList.remove('achieved');
        if (milestone.achieved) {
          thresholdEl.classList.add('achieved');
        }
      }
      
      if (labelEl) {
        labelEl.classList.remove('achieved', 'active');
        if (milestone.achieved) {
          labelEl.classList.add('achieved');
          labelEl.style.color = '#45851E';
        } else {
          labelEl.style.color = '';
        }
        
        // Update label text to include worth value
        if (milestone.worth) {
          labelEl.innerHTML = `${milestone.label}<br><small style="font-size: 0.8em; opacity: 0.8;">(Worth ${milestone.worth})</small>`;
        } else {
          labelEl.textContent = milestone.label;
        }
      }
    });
    
    // Determine current offer text based on cart total
    const currentOfferEl = document.querySelector('.gwp-bar__heading');
    if (currentOfferEl) {
      let offerText = '';
      let moneyAmount = '';
      
      if (cartTotal < 69) {
        const remaining = (69 - cartTotal).toFixed(2);
        offerText = `Just $${remaining} away from Free Shipping`;
        moneyAmount = `$${remaining}`;
      } else if (cartTotal >= 69 && cartTotal < 120) {
        const remaining = (120 - cartTotal).toFixed(2);
        offerText = `Just $${remaining} away from Free T-Shirt(Worth $30)`;
        moneyAmount = `$${remaining}`;
        // Mark next milestone as active
        const nextLabel = document.querySelector('.gwp-bar__labels li[data-forloop-index0="1"]');
        if (nextLabel) {
          nextLabel.classList.add('active');
        }
      } else if (cartTotal >= 120 && cartTotal < 199) {
        const remaining = (199 - cartTotal).toFixed(2);
        offerText = `Just $${remaining} away from Free SweatShirt(Worth $70)`;
        moneyAmount = `$${remaining}`;
        // Mark next milestone as active
        const nextLabel = document.querySelector('.gwp-bar__labels li[data-forloop-index0="2"]');
        if (nextLabel) {
          nextLabel.classList.add('active');
        }
      } else {
        // Cart total >= 199
        offerText = `Yay - You have unlocked all free gifts!`;
        moneyAmount = '';
      }
      
      // Clear any existing active classes
      document.querySelectorAll('.gwp-bar__labels li').forEach(label => {
        label.classList.remove('active');
      });
      
      // Hide the heading when progress is 100%
      if (progressPercentage >= 100) {
        currentOfferEl.style.display = 'none';
      } else {
        currentOfferEl.style.display = '';
        // Update the heading text completely
        if (moneyAmount) {
          currentOfferEl.innerHTML = offerText.replace(moneyAmount, `<span class="money">${moneyAmount}</span>`);
        } else {
          currentOfferEl.textContent = offerText;
        }
      }
      
      if (this.debugMode) {
        console.log('TieredOffers - Status text:', offerText);
      }
    }
    
    // Handle free product offers (for logging/future functionality)
    await this.handleFreeProductOffers(cartTotal);
    
    // TEMPORARILY DISABLED - Update cart total display with corrected amount
    // await this.updateCartTotalDisplay(cartTotal);
    } catch (error) {
      console.error('TieredOffers - Error in updateOffersDisplay:', error);
      // Continue with default behavior even if there's an error
    }
  }

  async updateCartTotalDisplay(correctedTotal) {
    try {
      if (this.debugMode) {
        console.log('TieredOffers - updateCartTotalDisplay called with:', correctedTotal, 'type:', typeof correctedTotal);
      }
      
      // Multiple layers of validation to ensure we have a valid number
      let totalAsNumber;
      
      if (correctedTotal === null || correctedTotal === undefined) {
        if (this.debugMode) {
          console.log('TieredOffers - correctedTotal is null/undefined, defaulting to 0');
        }
        totalAsNumber = 0;
      } else if (typeof correctedTotal === 'number' && !isNaN(correctedTotal)) {
        totalAsNumber = correctedTotal;
      } else {
        totalAsNumber = parseFloat(String(correctedTotal));
        if (isNaN(totalAsNumber)) {
          if (this.debugMode) {
            console.log('TieredOffers - Could not parse correctedTotal as number:', correctedTotal, 'defaulting to 0');
          }
          totalAsNumber = 0;
        }
      }
      
      // Final safety check
      if (typeof totalAsNumber !== 'number' || isNaN(totalAsNumber)) {
        console.error('TieredOffers - totalAsNumber is still not a valid number:', totalAsNumber, 'type:', typeof totalAsNumber);
        totalAsNumber = 0;
      }
      
      if (this.debugMode) {
        console.log('TieredOffers - Final totalAsNumber:', totalAsNumber, 'type:', typeof totalAsNumber);
      }
      
      // Update cart total elements to show corrected total
      const cartTotalElements = document.querySelectorAll('.cart__total-value, [data-cart-total], .cart-total');
      
      cartTotalElements.forEach((element, index) => {
        try {
          if (element && element.textContent) {
            // Triple-check before calling toFixed
            if (typeof totalAsNumber === 'number' && !isNaN(totalAsNumber) && totalAsNumber.toFixed) {
              const formattedTotal = `$${totalAsNumber.toFixed(2)}`;
              element.textContent = formattedTotal;
              
              if (this.debugMode) {
                console.log(`TieredOffers - Updated element ${index} to: ${formattedTotal}`);
              }
            } else {
              console.error('TieredOffers - totalAsNumber failed final validation:', totalAsNumber, 'type:', typeof totalAsNumber);
              element.textContent = '$0.00'; // Fallback
            }
          }
        } catch (elementError) {
          console.error('TieredOffers - Error updating individual element:', elementError, 'element:', element, 'totalAsNumber:', totalAsNumber);
        }
      });

      // Also update any cart bubble/count displays
      const cartBubbles = document.querySelectorAll('.cart-bubble, [data-cart-count]');
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      cartBubbles.forEach(bubble => {
        if (bubble.textContent !== undefined) {
          bubble.textContent = cart.item_count;
        }
      });

    } catch (error) {
      console.error('TieredOffers - Error updating cart total display:', error);
    }
  }

  async handleFreeProductOffers(cartTotal) {
    // Check which offers are already claimed by looking at current cart
    const alreadyClaimed = await this.getAlreadyClaimedOffers();
    
    const freeProductOffers = this.milestones.filter(m => 
      m.productHandle && 
      m.achieved && 
      !this.addedFreeItems.has(m.productHandle) &&
      !alreadyClaimed.has(m.label)
    );
    
    if (this.debugMode) {
      console.log('TieredOffers - Free product offers available:', freeProductOffers.map(o => o.label));
      console.log('TieredOffers - Already claimed offers:', Array.from(alreadyClaimed));
    }
    
    const selectionContainer = document.querySelector('[data-free-product-selection]');
    if (!selectionContainer) return;
    
    if (freeProductOffers.length === 0) {
      selectionContainer.style.display = 'none';
      selectionContainer.innerHTML = '';
      return;
    }
    
    // Show and build product selection UI
    selectionContainer.style.display = 'block';
    
    let selectionHTML = '<div class="free-products-container">';
    for (const offer of freeProductOffers) {
      try {
        const productData = await this.fetchProductDataByHandle(offer.productHandle);
        if (productData) {
          selectionHTML += this.buildProductSelectionHTML(productData, offer);
        }
      } catch (error) {
        console.error('TieredOffers - Error fetching product data:', error);
      }
    }
    selectionHTML += '</div>';
    
    selectionContainer.innerHTML = selectionHTML;
    this.setupProductSelectionListeners();
  }

  async getAlreadyClaimedOffers() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      const claimedOffers = new Set();
      
      if (cart && cart.items) {
        cart.items.forEach(item => {
          if (item.properties && item.properties._free === 'true' && item.properties._offer_type) {
            claimedOffers.add(item.properties._offer_type);
          }
        });
      }
      
      return claimedOffers;
    } catch (error) {
      console.error('TieredOffers - Error checking claimed offers:', error);
      return new Set();
    }
  }

  async fetchProductDataByHandle(productHandle) {
    try {
      if (this.debugMode) {
        console.log(`TieredOffers - Fetching product by handle: ${productHandle}`);
      }
      
      const response = await fetch(`/products/${productHandle}.js`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const productData = await response.json();
      
      if (this.debugMode) {
        console.log(`TieredOffers - Successfully fetched product: ${productData.title}`);
      }
      
      return productData;
    } catch (error) {
      console.error(`TieredOffers - Error fetching product ${productHandle}:`, error);
      return null;
    }
  }

  async fetchProductData(productId) {
    try {
      const response = await fetch(`/products/${productId}.js`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error(`TieredOffers - Error fetching product ${productId}:`, error);
      return null;
    }
  }

  buildProductSelectionHTML(product, offer) {
    const variants = product.variants || [];
    const availableVariants = variants.filter(v => v.available);
    
    if (availableVariants.length === 0) {
      return ''; // Skip if no variants available
    }
    
    // Set the correct original price based on the offer type
    let originalPrice;
    if (offer.label === 'Free T-Shirt') {
      originalPrice = 2999; // $29.99 in cents
    } else if (offer.label === 'Free SweatShirt') {
      originalPrice = 7000; // $70.00 in cents
    } else {
      originalPrice = product.variants[0]?.price || 0;
    }

    // Determine default selected size using cookie → XL → L → first available
    const preferredSize = this.getPreferredSizeFromCookie?.() || '';
    const normalize = (s) => String(s || '').trim().toUpperCase().replace(/\s+/g, '');
    const getSizeOnly = (title) => String(title || '').split(' / ')[0];
    const findVariantIdBySize = (sizeLabel) => {
      if (!sizeLabel) return null;
      const target = normalize(sizeLabel);
      const match = availableVariants.find(v => normalize(getSizeOnly(v.title)) === target);
      return match ? match.id : null;
    };

    let selectedVariantId =
      findVariantIdBySize(preferredSize) ||
      findVariantIdBySize('XL') ||
      findVariantIdBySize('L') ||
      (availableVariants[0]?.id || null);

    return `
      <div class="addon-item" data-product-handle="${offer.productHandle}">
        <div class="">
          <div class="addon-item__image-container">
            <img class="addon-item__image" src="${product.featured_image}" alt="${product.title}" loading="lazy">
          </div>
          <div class="addon-item__content">
            <div class="addon-item__title">${product.title}</div>
            <div class="addon-item__price">
              <span class="price--original">$${this.formatPrice(originalPrice)}</span>
              <span class="price--free">FREE</span>
            </div>
            <form class="addon-item__form" data-product-form="${offer.productHandle}">
              <div class="addon-item__size-selector">
                <select name="variant" required>
                  <option value="">Choose Size</option>
                  ${availableVariants.map(variant => {
                    const sizeOnly = getSizeOnly(variant.title);
                    const isSelected = variant.id === selectedVariantId ? 'selected' : '';
                    return `<option value="${variant.id}" ${isSelected}>${sizeOnly}</option>`;
                  }).join('')}
                </select>
              </div>
              <button type="submit" class="addon-item__add-btn">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none">
                  <path d="M3 12H21M21 12L14 19M21 12L14 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span>Add</span>
              </button>
              <input type="hidden" name="quantity" value="1">
              <input type="hidden" name="properties[_free]" value="true">
              <input type="hidden" name="properties[_offer_type]" value="${offer.label}">
              <input type="hidden" name="properties[_product_handle]" value="${offer.productHandle}">
            </form>
          </div>
        </div>
      </div>
    `;
  }

  // Helper: read preferred size cookie used elsewhere
  getPreferredSizeFromCookie() {
    try {
      const name = 'preferred_size=';
      const parts = document.cookie.split(';');
      for (let part of parts) {
        part = part.trim();
        if (part.startsWith(name)) {
          const raw = decodeURIComponent(part.substring(name.length));
          return String(raw || '').trim().toUpperCase().replace(/\s+/g, '');
        }
      }
      return '';
    } catch (e) {
      return '';
    }
  }

  setupProductSelectionListeners() {
    const selectionContainer = document.querySelector('[data-free-product-selection]');
    if (!selectionContainer) return;
    
    // Handle size selection changes
    selectionContainer.querySelectorAll('select[name="variant"]').forEach(select => {
      select.addEventListener('change', (e) => {
        const form = e.target.closest('form');
        const addBtn = form.querySelector('.addon-item__add-btn');
        addBtn.disabled = !e.target.value;
      });
    });
    
    // Handle form submissions
    selectionContainer.querySelectorAll('[data-product-form]').forEach(form => {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        
        const productHandle = form.dataset.productForm;
        const variantSelect = form.querySelector('select[name="variant"]');
        const addBtn = form.querySelector('.addon-item__add-btn');
        const originalText = addBtn.querySelector('span').textContent;
        
        const variantId = variantSelect.value;
        if (!variantId) return;
        
        // Update button state
        addBtn.disabled = true;
        addBtn.querySelector('span').textContent = 'Adding...';
        
        try {
          const success = await this.addFreeProductToCart(variantId, {
            '_free': 'true',
            '_offer_type': form.querySelector('input[name="properties[_offer_type]"]').value
          });
          
          if (success) {
            // Mark as added and remove from UI
            this.addedFreeItems.add(productHandle);
            
            addBtn.classList.add('added');
            addBtn.querySelector('span').textContent = '✓ Added!';
            
            // Immediately refresh cart items only (preserve progress bar state)
            await this.refreshCartItemsOnly();
            
            // Remove the item after a short delay
            setTimeout(() => {
              const addonItem = form.closest('.addon-item');
              if (addonItem) {
                addonItem.style.opacity = '0';
                addonItem.style.transform = 'translateY(-10px)';
                addonItem.style.transition = 'all 0.3s ease';
                setTimeout(() => {
                  addonItem.remove();
                  // Hide container if no more items
                  const freeProductsContainer = selectionContainer.querySelector('.free-products-container');
                  if (!freeProductsContainer || !freeProductsContainer.querySelector('.addon-item')) {
                    selectionContainer.style.display = 'none';
                  }
                }, 300);
              }
            }, 1000);
          } else {
            // Reset button on failure
            addBtn.disabled = false;
            addBtn.querySelector('span').textContent = originalText;
          }
        } catch (error) {
          console.error('TieredOffers - Error adding product:', error);
          addBtn.disabled = false;
          addBtn.querySelector('span').textContent = originalText;
        }
      });
    });
  }

  async addFreeProductToCart(variantId, properties = {}) {
    try {
      // Add the item to cart normally - we'll handle the pricing correction in getCartTotal()
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: 1,
            properties: properties
          }]
        })
      });
      
      if (response.ok) {
        if (this.debugMode) {
          console.log('TieredOffers - Successfully added free product to cart (pricing will be corrected in display)');
        }
        return true;
      } else {
        console.error('TieredOffers - Failed to add product to cart:', response.statusText);
        return false;
      }
    } catch (error) {
      console.error('TieredOffers - Error adding free product to cart:', error);
      return false;
    }
  }

  formatPrice(cents) {
    return (cents / 100).toFixed(2);
  }

  formatMoney(cents) {
    return `$${(cents / 100).toFixed(2)}`;
  }

  async refreshCartItemsOnly() {
    try {
      if (this.debugMode) {
        console.log('TieredOffers - Adding new free item to cart without refresh...');
      }
      
      // Get updated cart data
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      if (this.debugMode) {
        console.log('TieredOffers - Cart data received:', cart);
        console.log('TieredOffers - Cart items:', cart.items);
      }
      
      // Update cart bubble/counter
      const cartBubbles = document.querySelectorAll('.cart-bubble, [data-cart-count]');
      cartBubbles.forEach(bubble => {
        if (bubble.textContent !== undefined) {
          bubble.textContent = cart.item_count;
        }
      });
      
      // Find the newly added free item (should be the last item with _free property)
      const newFreeItem = cart.items.find(item => 
        item.properties && item.properties._free === 'true' && 
        !this.isItemAlreadyDisplayed(item)
      );
      
      if (this.debugMode) {
        console.log('TieredOffers - New free item found:', newFreeItem);
      }
      
      if (newFreeItem) {
        await this.addNewFreeItemToDisplay(newFreeItem);
      } else {
        console.log('TieredOffers - No new free item found to display');
      }
      
      // Use corrected cart total for display
      const correctedTotal = await this.getCartTotal();
      
      if (this.debugMode) {
        console.log('TieredOffers - refreshCartItemsOnly correctedTotal:', correctedTotal, 'type:', typeof correctedTotal);
      }
      
      this.updateOffersDisplay(correctedTotal);
      
      if (this.debugMode) {
        console.log('TieredOffers - New free item added, cart total:', cart.total_price);
      }
      
    } catch (error) {
      console.error('TieredOffers - Error adding free item to display:', error);
    }
  }

  isItemAlreadyDisplayed(cartItem) {
    // Check if this cart item is already displayed in the cart
    const existingRows = document.querySelectorAll('.cart-items__table-row');
    return Array.from(existingRows).some(row => {
      const variantId = row.dataset.variantId;
      const isFree = row.querySelector('.cart-item__free-price');
      return variantId === cartItem.variant_id.toString() && isFree;
    });
  }

  async addNewFreeItemToDisplay(cartItem) {
    try {
      if (this.debugMode) {
        console.log('TieredOffers - Adding new free item to display:', cartItem);
      }
      
      // Calculate the correct item index for the new item
      const itemIndex = cartItem.index || cartItem.line || (document.querySelectorAll('.cart-items__table-row').length + 1);
      
      // Get the cart items table - try multiple selectors
      let cartTable = document.querySelector('.cart-items__table tbody');
      
      if (!cartTable) {
        // Fallback: try to find any tbody in the cart
        cartTable = document.querySelector('.cart-items tbody');
      }
      
      if (!cartTable) {
        // Fallback: try to find the table and create tbody if needed
        const table = document.querySelector('.cart-items__table');
        if (table) {
          cartTable = table.querySelector('tbody') || table.appendChild(document.createElement('tbody'));
        }
      }
      
      if (!cartTable) {
        console.error('TieredOffers - Cart table not found. Available elements:', {
          cartItemsTable: document.querySelector('.cart-items__table'),
          cartItems: document.querySelector('.cart-items'),
          allTables: document.querySelectorAll('table')
        });
        return;
      }
      
      if (this.debugMode) {
        console.log('TieredOffers - Cart table found:', cartTable);
        console.log('TieredOffers - Existing rows:', cartTable.children.length);
        console.log('TieredOffers - Item index for new row:', itemIndex);
      }
      
      // Create table row element directly and safely
      const newRow = document.createElement('tr');
      newRow.setAttribute('role', 'row');
      newRow.className = 'cart-items__table-row';
      newRow.setAttribute('ref', 'cartItemRows[]');
      newRow.setAttribute('data-line-index', itemIndex);
      newRow.setAttribute('data-variant-id', cartItem.variant_id);
      newRow.setAttribute('data-product-id', cartItem.product_id);
      newRow.setAttribute('data-original-price', cartItem.original_price);
      newRow.setAttribute('data-quantity', '1'); // Force tiered offer items to always be quantity 1
      newRow.setAttribute('data-tiered-offer-item', 'true'); // Exclude from buy3get1 processing
      
      // Use innerHTML for the content, but on a proper TR element
      newRow.innerHTML = this.buildCartItemCellsHTML(cartItem, itemIndex);
      
      if (this.debugMode) {
        console.log('TieredOffers - Created table row element:', newRow);
      }
      
      if (this.debugMode) {
        console.log('TieredOffers - New row element created:', newRow);
      }
      
      // Try multiple insertion methods for maximum compatibility
      try {
        cartTable.appendChild(newRow);
        if (this.debugMode) {
          console.log('TieredOffers - Row appended successfully');
        }
      } catch (appendError) {
        console.error('TieredOffers - Failed to append row:', appendError);
        return;
      }
      
      // Use the newly added row for animation
      const actualNewRow = newRow;
      
      // Add entrance animation
      if (actualNewRow) {
        actualNewRow.style.opacity = '0';
        actualNewRow.style.transform = 'translateY(10px)';
        
        // Use requestAnimationFrame for better animation timing
        requestAnimationFrame(() => {
          actualNewRow.style.transition = 'all 0.3s ease';
          actualNewRow.style.opacity = '1';
          actualNewRow.style.transform = 'translateY(0)';
        });
      }
      
      if (this.debugMode) {
        console.log('TieredOffers - New free item row added to display successfully');
      }
      
    } catch (error) {
      console.error('TieredOffers - Error building free item row:', error);
      console.error('TieredOffers - Error details:', error.stack);
    }
  }

  buildCartItemCellsHTML(cartItem, itemIndex = null) {
    const calculatedItemIndex = itemIndex || cartItem.index || 1;
    const productTitle = cartItem.product_title || cartItem.title;
    
    // Set the correct original price for free items based on the product
    let price;
    if (cartItem.properties && cartItem.properties._free === 'true') {
      // Check if it's a free polo or quarter zip based on the product handle or offer type
      const offerType = cartItem.properties._offer_type;
      if (offerType === 'Free T-Shirt') {
        price = '$29.99';
      } else if (offerType === 'Free SweatShirt') {
        price = '$70';
      } else {
        price = this.formatMoney(cartItem.original_price);
      }
    } else {
      // Use final line price (total for the line item) instead of per-unit price
      price = this.formatMoney(cartItem.final_line_price || cartItem.original_price);
    }
    
    const productUrl = cartItem.url || `/products/${cartItem.handle || 'unknown'}?variant=${cartItem.variant_id}`;
    let imageUrl = cartItem.image || cartItem.featured_image;
    
    // Fix image URL for Shopify CDN
    if (imageUrl) {
      // Ensure proper CDN URL format with width parameter
      if (imageUrl.includes('cdn.shopify.com') || imageUrl.includes('shopify.com/cdn')) {
        if (!imageUrl.includes('width=')) {
          imageUrl += imageUrl.includes('?') ? '&width=250' : '?width=250';
        }
      }
    }
    
    // Parse variant options properly
    let variantOptionsHTML = '';
    if (cartItem.options_with_values && cartItem.options_with_values.length > 0) {
      const variantPairs = cartItem.options_with_values.map(option => 
        `<div class="cart-items__variant">
          <dt class="visually-hidden">${option.name}:</dt>
          <dd>${option.value}${cartItem.options_with_values.indexOf(option) < cartItem.options_with_values.length - 1 ? ',&nbsp;' : ''}</dd>
        </div>`
      ).join('');
      variantOptionsHTML = `<dl class="cart-items__variants">${variantPairs}</dl>`;
    } else if (cartItem.variant_title && cartItem.variant_title !== 'Default Title') {
      // Fallback for basic variant title
      const variants = cartItem.variant_title.split(' / ');
      const variantPairs = variants.map((variant, index) => {
        const optionName = index === 0 ? 'Size' : index === 1 ? 'Color' : 'Option';
        return `<div class="cart-items__variant">
          <dt class="visually-hidden">${optionName}:</dt>
          <dd>${variant}${index < variants.length - 1 ? ',&nbsp;' : ''}</dd>
        </div>`;
      }).join('');
      variantOptionsHTML = `<dl class="cart-items__variants">${variantPairs}</dl>`;
    }
    
    return `
        <td class="cart-items__media" role="cell" headers="productImage">
          ${imageUrl ? `
            <a href="${productUrl}" class="cart-items__media-container" style="--ratio:1;">
              <img src="${imageUrl}" 
                   alt="${productTitle}${cartItem.variant_title && cartItem.variant_title !== 'Default Title' ? ' - ' + cartItem.variant_title : ''}" 
                   srcset="${imageUrl} 250w"
                   width="250" 
                   height="313" 
                   class="cart-items__media-image">
            </a>
          ` : ''}
        </td>
        <td class="cart-items__details cart-primary-typography" role="cell" headers="productInformation">
          <p>
            <a href="${productUrl}" class="cart-items__title">${productTitle}</a>
          </p>
          ${variantOptionsHTML}
          
          <!-- Tiered Bundle Offers Indicator -->
          <div class="cart-item__buy3get1-indicator hidden" data-free-item-indicator>
            IN FREE OFFER- Bundle &amp; Save
          </div>

          <div class="cart-item__pricing" data-item-pricing>
            <!-- Free item pricing -->
            <s class="cart-item__compare-price">${price}</s>
            <span class="cart-item__current-price cart-item__free-price" style="color: #6fae4a; font-weight: 600; font-size: 12px;">FREE</span>
          </div>
        </td>
        <td class="cart-items__quantity" role="cell" headers="quantity">
          <!-- Static quantity display for free items -->
          <div class="free-item-quantity">
            <span class="free-item-quantity-text">1</span>
          </div>

          <button class="button button--tertiary cart-items__remove" type="button" 
                  aria-label="Remove ${productTitle}${cartItem.variant_title && cartItem.variant_title !== 'Default Title' ? ' - ' + cartItem.variant_title : ''}" 
                  onclick="window.tieredOffersManager?.removeItemAndUpdate?.(this);">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path class="remove-icon-bottom" d="M11.5 9v4.25M8.5 9v4.25M5.75 12.2V6h8.5c0 2.421 0 3.779 0 6.2 0 .853 0 1.447-.038 1.91-.037.453-.106.714-.207.911a2.498 2.498 0 0 1-.983 1.017c-.197.1-.458.17-.911.207-.463.037-1.057.038-1.91.038h-.4c-.853 0-1.447 0-1.91-.038-.453-.037-.714-.106-.911-.207a2.498 2.498 0 0 1-.984-1.017c-.1-.197-.17-.458-.207-.911C5.75 13.647 5.75 13.053 5.75 12.2z" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"></path>
              <path class="remove-icon-top" d="M4.25 6h11.5M8 5.25a2 2 0 1 1 4 0" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"></path>
            </svg>
            <span class="visually-hidden">Remove</span>
          </button>
        </td>
        <td class="cart-items__error hidden" role="cell" ref="cartItemErrorContainer-${calculatedItemIndex}" headers="quantity">
          <div class="cart-item__error" role="alert">
            <span class="svg-wrapper">
              <svg class="icon icon-error" viewBox="0 0 13 13">
                <circle cx="6.5" cy="6.5" r="5.5" stroke="#fff" stroke-width="var(--icon-stroke-width)"></circle>
                <circle cx="6.5" cy="6.5" r="5.5" fill="#EB001B" stroke="#EB001B" stroke-width=".7"></circle>
                <path fill="#fff" d="m5.874 3.528.1 4.044h1.053l.1-4.044zm.627 6.133c.38 0 .68-.288.68-.656s-.3-.656-.68-.656-.681.288-.681.656.3.656.68.656"></path>
                <path fill="#fff" stroke="#EB001B" stroke-width=".7" d="M5.874 3.178h-.359l.01.359.1 4.044.008.341h1.736l.008-.341.1-4.044.01-.359H5.873Zm.627 6.833c.56 0 1.03-.432 1.03-1.006s-.47-1.006-1.03-1.006-1.031.432-1.031 1.006.47 1.006 1.03 1.006Z"></path>
              </svg>
            </span>
            <small class="cart-item__error-text cart-primary-typography" ref="cartItemError-${calculatedItemIndex}"></small>
          </div>
        </td>
        <td class="cart-items__price cart-secondary-typography" role="cell" headers="productTotal">
          <text-component value="FREE">FREE</text-component>
        </td>
    `;
  }

  async buildCartItemRowHTML(cartItem) {
    const itemIndex = cartItem.index || 1;
    const productTitle = cartItem.product_title || cartItem.title;
    
    // Set the correct original price for free items based on the product
    let price;
    if (cartItem.properties && cartItem.properties._free === 'true') {
      // Check if it's a free polo or quarter zip based on the product handle or offer type
      const offerType = cartItem.properties._offer_type;
      if (offerType === 'Free T-Shirt') {
        price = '$29.99';
      } else if (offerType === 'Free SweatShirt') {
        price = '$35.99';
      } else {
        price = this.formatMoney(cartItem.original_price);
      }
    } else {
      // Use final line price (total for the line item) instead of per-unit price
      price = this.formatMoney(cartItem.final_line_price || cartItem.original_price);
    }
    
    const productUrl = cartItem.url || `/products/${cartItem.handle || 'unknown'}?variant=${cartItem.variant_id}`;
    let imageUrl = cartItem.image || cartItem.featured_image;
    
    // Fix image URL for Shopify CDN
    if (imageUrl) {
      // Ensure proper CDN URL format with width parameter
      if (imageUrl.includes('cdn.shopify.com') || imageUrl.includes('shopify.com/cdn')) {
        if (!imageUrl.includes('width=')) {
          imageUrl += imageUrl.includes('?') ? '&width=250' : '?width=250';
        }
      }
    }
    
    // Parse variant options properly
    let variantOptionsHTML = '';
    if (cartItem.options_with_values && cartItem.options_with_values.length > 0) {
      const variantPairs = cartItem.options_with_values.map(option => 
        `<div class="cart-items__variant">
          <dt class="visually-hidden">${option.name}:</dt>
          <dd>${option.value}${cartItem.options_with_values.indexOf(option) < cartItem.options_with_values.length - 1 ? ',&nbsp;' : ''}</dd>
        </div>`
      ).join('');
      variantOptionsHTML = `<dl class="cart-items__variants">${variantPairs}</dl>`;
    } else if (cartItem.variant_title && cartItem.variant_title !== 'Default Title') {
      // Fallback for basic variant title
      const variants = cartItem.variant_title.split(' / ');
      const variantPairs = variants.map((variant, index) => {
        const optionName = index === 0 ? 'Size' : index === 1 ? 'Color' : 'Option';
        return `<div class="cart-items__variant">
          <dt class="visually-hidden">${optionName}:</dt>
          <dd>${variant}${index < variants.length - 1 ? ',&nbsp;' : ''}</dd>
        </div>`;
      }).join('');
      variantOptionsHTML = `<dl class="cart-items__variants">${variantPairs}</dl>`;
    }
    
    if (this.debugMode) {
      console.log('TieredOffers - Building cart item HTML for:', productTitle, cartItem);
    }
    
    return `
      <tr role="row" class="cart-items__table-row" 
          ref="cartItemRows[]"
          data-line-index="${itemIndex}" 
          data-variant-id="${cartItem.variant_id}"
          data-product-id="${cartItem.product_id}"
          data-original-price="${cartItem.original_price}"
          data-quantity="${cartItem.quantity}">
        <td class="cart-items__media" role="cell" headers="productImage">
          ${imageUrl ? `
            <a href="${productUrl}" class="cart-items__media-container" style="--ratio:1;">
              <img src="${imageUrl}" 
                   alt="${productTitle}${cartItem.variant_title && cartItem.variant_title !== 'Default Title' ? ' - ' + cartItem.variant_title : ''}" 
                   srcset="${imageUrl} 250w"
                   width="250" 
                   height="313" 
                   class="cart-items__media-image">
            </a>
          ` : ''}
        </td>
        <td class="cart-items__details cart-primary-typography" role="cell" headers="productInformation">
          <p>
            <a href="${productUrl}" class="cart-items__title">${productTitle}</a>
          </p>
          ${variantOptionsHTML}
          
          <!-- Tiered Bundle Offers Indicator -->
          <div class="cart-item__buy3get1-indicator hidden" data-free-item-indicator>
            IN FREE OFFER- Bundle &amp; Save
          </div>

          <div class="cart-item__pricing" data-item-pricing>
            <!-- Free item pricing -->
            <s class="cart-item__compare-price">${price}</s>
            <span class="cart-item__current-price cart-item__free-price" style="color: #6fae4a; font-weight: 600; font-size: 12px;">FREE</span>
            <small class="cart-item__free-note">Free Gift</small>
          </div>
        </td>
        <td class="cart-items__quantity" role="cell" headers="quantity">
          <!-- Static quantity display for free items -->
          <div class="free-item-quantity">
            <span class="free-item-quantity-text">1</span>
            <small class="free-item-note">Free Gift</small>
          </div>

          <button class="button button--tertiary cart-items__remove" type="button" 
                  aria-label="Remove ${productTitle}${cartItem.variant_title && cartItem.variant_title !== 'Default Title' ? ' - ' + cartItem.variant_title : ''}" 
                  onclick="window.tieredOffersManager?.removeItemAndUpdate?.(this);">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path class="remove-icon-bottom" d="M11.5 9v4.25M8.5 9v4.25M5.75 12.2V6h8.5c0 2.421 0 3.779 0 6.2 0 .853 0 1.447-.038 1.91-.037.453-.106.714-.207.911a2.498 2.498 0 0 1-.983 1.017c-.197.1-.458.17-.911.207-.463.037-1.057.038-1.91.038h-.4c-.853 0-1.447 0-1.91-.038-.453-.037-.714-.106-.911-.207a2.498 2.498 0 0 1-.984-1.017c-.1-.197-.17-.458-.207-.911C5.75 13.647 5.75 13.053 5.75 12.2z" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"></path>
              <path class="remove-icon-top" d="M4.25 6h11.5M8 5.25a2 2 0 1 1 4 0" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"></path>
            </svg>
            <span class="visually-hidden">Remove</span>
          </button>
        </td>
        <td class="cart-items__error hidden" role="cell" ref="cartItemErrorContainer-${itemIndex}" headers="quantity">
          <div class="cart-item__error" role="alert">
            <span class="svg-wrapper">
              <svg class="icon icon-error" viewBox="0 0 13 13">
                <circle cx="6.5" cy="6.5" r="5.5" stroke="#fff" stroke-width="var(--icon-stroke-width)"></circle>
                <circle cx="6.5" cy="6.5" r="5.5" fill="#EB001B" stroke="#EB001B" stroke-width=".7"></circle>
                <path fill="#fff" d="m5.874 3.528.1 4.044h1.053l.1-4.044zm.627 6.133c.38 0 .68-.288.68-.656s-.3-.656-.68-.656-.681.288-.681.656.3.656.68.656"></path>
                <path fill="#fff" stroke="#EB001B" stroke-width=".7" d="M5.874 3.178h-.359l.01.359.1 4.044.008.341h1.736l.008-.341.1-4.044.01-.359H5.873Zm.627 6.833c.56 0 1.03-.432 1.03-1.006s-.47-1.006-1.03-1.006-1.031.432-1.031 1.006.47 1.006 1.03 1.006Z"></path>
              </svg>
            </span>
            <small class="cart-item__error-text cart-primary-typography" ref="cartItemError-${itemIndex}"></small>
          </div>
        </td>
        <td class="cart-items__price cart-secondary-typography" role="cell" headers="productTotal">
          <text-component value="FREE">FREE</text-component>
        </td>
      </tr>
    `;
  }

  async refreshCartDisplay() {
    try {
      if (this.debugMode) {
        console.log('TieredOffers - Refreshing cart display using section renderer...');
      }
      
      // Method 1: Use the theme's section renderer (most reliable)
      const cartItemsComponent = document.querySelector('cart-items-component');
      if (cartItemsComponent && cartItemsComponent.dataset.sectionId) {
        const sectionId = cartItemsComponent.dataset.sectionId;
        
        // Import and use the section renderer if available
        if (window.sectionRenderer && typeof window.sectionRenderer.renderSection === 'function') {
          window.sectionRenderer.renderSection(sectionId, { cache: false });
          return;
        }
      }
      
      // Method 2: Dispatch the proper theme cart update event
      const { CartUpdateEvent } = await import('@theme/events').catch(() => ({}));
      if (CartUpdateEvent) {
        document.dispatchEvent(new CartUpdateEvent({}, 'cart-drawer', {
          source: 'tiered-offers',
          itemCount: null // Will be fetched
        }));
      } else {
        // Fallback to standard event
        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { source: 'tiered-offers' }
        }));
      }
      
      if (this.debugMode) {
        console.log('TieredOffers - Cart display refresh completed');
      }
      
    } catch (error) {
      console.error('TieredOffers - Error refreshing cart display:', error);
      // Final fallback
      await this.reloadCartSection();
    }
  }

  async reloadCartSection() {
    try {
      // Simple approach: trigger the existing cart refresh mechanisms
      if (this.debugMode) {
        console.log('TieredOffers - Triggering cart section reload');
      }
      
      // Try multiple event approaches used by Shopify themes
      const events = [
        'cart:updated',
        'cartUpdated', 
        'cart:refresh',
        'theme:cart:update',
        new CustomEvent('cart:updated', { detail: { source: 'tiered-offers' } })
      ];
      
      events.forEach(event => {
        if (typeof event === 'string') {
          document.dispatchEvent(new CustomEvent(event));
        } else {
          document.dispatchEvent(event);
        }
      });
      
      console.log('TieredOffers - Cart refresh events dispatched');
      
    } catch (error) {
      console.error('TieredOffers - Error reloading cart section:', error);
    }
  }

  handleCartUpdate(event) {
    if (this.debugMode) {
      console.log('TieredOffers - Cart updated, source:', event?.detail?.source || 'unknown');
    }
    
    // If this update is from our own tiered offers system, handle it more gently
    if (event?.detail?.source === 'tiered-offers' || event?.detail?.source === 'tiered-offers-removal' || event?.detail?.preserveOffers) {
      if (this.debugMode) {
        console.log('TieredOffers - Update from tiered offers system, updating display only');
      }
      
      // Just update the display with current cart total, don't reinitialize everything
      if (event.detail?.cart?.total_price !== undefined) {
        this.updateOffersDisplay(event.detail.cart.total_price / 100);
      }
      return;
    }
    
    // For external cart updates, only update the display - don't check for removals immediately
    // This prevents interference with normal cart operations
    if (this.debugMode) {
      console.log('TieredOffers - External cart update detected, updating display only');
    }
    
    // Only update the offers display, don't interfere with cart operations
    setTimeout(() => {
      this.updateOffersDisplay();
    }, 100); // Short delay just to ensure DOM is updated
  }

  handleCartDrawerOpened(event) {
    if (this.debugMode) {
      console.log('TieredOffers - Cart drawer opened, updating offers display');
    }
    
    // Update the offers display when cart drawer opens
    // Use a short delay to ensure the cart content is fully loaded
    setTimeout(() => {
      this.updateOffersDisplay();
    }, 200);
  }

  setupCartDrawerObserver() {
    // Watch for cart drawer content changes
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (!cartDrawer) return;

    const observer = new MutationObserver((mutations) => {
      let shouldUpdate = false;
      
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          // Check if cart items or tiered offers content was added
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node;
              if (element.querySelector && element.querySelector('.cart-items__table, .gwp-bar, .tiered-offers-container')) {
                shouldUpdate = true;
              }
            }
          });
        }
      });
      
      if (shouldUpdate) {
        if (this.debugMode) {
          console.log('TieredOffers - Cart drawer content changed, updating offers display');
        }
        setTimeout(() => {
          this.updateOffersDisplay();
        }, 100);
      }
    });

    observer.observe(cartDrawer, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Handles quantity selector updates to immediately check for removal eligibility
   */
  handleQuantityChange(event) {
    if (this.debugMode) {
      console.log('TieredOffers - Quantity changed:', event.detail);
    }
    
    // Trigger removal check after a short delay to ensure cart is updated
    setTimeout(async () => {
      await this.checkAndRemoveIneligibleFreeItems();
    }, 300);
  }

  /**
   * Manual method to safely check for removal eligibility
   * Can be called when needed without interfering with ongoing cart operations
   */
  async manualRemovalCheck() {
    if (this.debugMode) {
      console.log('TieredOffers - Manual removal check triggered');
    }
    
    // Add a delay to ensure any ongoing cart operations are complete
    setTimeout(async () => {
      await this.checkAndRemoveIneligibleFreeItems();
      this.updateOffersDisplay();
    }, 500);
  }

  async checkAndRemoveIneligibleFreeItems() {
    try {
      if (this.debugMode) {
        console.log('TieredOffers - Checking for ineligible free items to remove');
      }

      // Get current cart and calculate corrected total
      const response = await fetch('/cart.js');
      const cart = await response.json();
      const correctedCartTotal = await this.getCartTotal();
      const cartTotalInCents = correctedCartTotal * 100; // Convert to cents for comparison

      // Find free items that should be removed
      const itemsToRemove = [];
      
      cart.items.forEach((item, index) => {
        if (item.properties && item.properties._free === 'true' && item.properties._offer_type) {
          const offerType = item.properties._offer_type;
          
          // Check if corrected cart total still qualifies for this offer
          let shouldRemove = false;
          
          if (offerType === 'Free T-Shirt' && cartTotalInCents < 12000) { // $120.00 in cents
            shouldRemove = true;
          } else if (offerType === 'Free SweatShirt' && cartTotalInCents < 19900) { // $199.00 in cents
            shouldRemove = true;
          }
          
          if (shouldRemove) {
            itemsToRemove.push({
              lineIndex: index + 1, // Shopify uses 1-based indexing
              title: item.product_title || item.title,
              offerType: offerType
            });
          }
        }
      });

      // Remove ineligible items
      for (const item of itemsToRemove) {
        if (this.debugMode) {
          console.log(`TieredOffers - Removing ineligible ${item.offerType}: ${item.title}`);
        }
        
        const success = await this.removeCartItem(item.lineIndex);
        
        if (success) {
          // Also remove from our tracking
          if (item.offerType === 'Free T-Shirt') {
            this.addedFreeItems.delete('the-signature-crew-white-copy');
          } else if (item.offerType === 'Free SweatShirt') {
            this.addedFreeItems.delete('unisex-garment-dyed-crewneck-sweatshirt-copy');
          }
        }
      }

      if (itemsToRemove.length > 0) {
        if (this.debugMode) {
          console.log(`TieredOffers - Removed ${itemsToRemove.length} ineligible free items`);
        }
        
        // Get updated cart total after removals and refresh display
        setTimeout(async () => {
          const newCorrectedCartTotal = await this.getCartTotal();
          if (this.debugMode) {
            console.log(`TieredOffers - New corrected cart total after removals:`, newCorrectedCartTotal, 'type:', typeof newCorrectedCartTotal);
          }
          
          // Refresh the entire cart display to ensure everything is updated
          await this.refreshCartDisplay();
          this.updateOffersDisplay(newCorrectedCartTotal);
          
          // Dispatch a cart update event to notify other components
          document.dispatchEvent(new CustomEvent(ThemeEvents.cartUpdate, {
            detail: { 
              source: 'tiered-offers-removal',
              cart: { total_price: newCorrectedCartTotal * 100 } // Convert back to cents
            }
          }));
        }, 800); // Longer delay to ensure all operations complete
      }

    } catch (error) {
      console.error('TieredOffers - Error checking/removing ineligible free items:', error);
    }
  }

  async removeFreeItemByVariantId(variantId) {
    try {
      // Get current cart to find the correct line index
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Find the free item with this variant ID
      let targetLineIndex = null;
      cart.items.forEach((item, index) => {
        if (item.variant_id.toString() === variantId.toString() && 
            item.properties && item.properties._free === 'true') {
          targetLineIndex = index + 1; // Shopify uses 1-based indexing
          if (this.debugMode) {
            console.log(`TieredOffers - Found free item to remove: ${item.product_title} at line ${targetLineIndex}`);
          }
        }
      });

      if (targetLineIndex) {
        const success = await this.removeCartItem(targetLineIndex);
        if (success) {
          // Update our tracking
          const productHandle = cart.items[targetLineIndex - 1]?.handle;
          if (productHandle) {
            this.addedFreeItems.delete(productHandle);
          }
        }
        return success;
      } else {
        console.error('TieredOffers - Free item not found in cart for variant ID:', variantId);
        return false;
      }
    } catch (error) {
      console.error('TieredOffers - Error removing free item by variant ID:', error);
      return false;
    }
  }

  async removeCartItem(lineIndex) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line: lineIndex,
          quantity: 0
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return true;
    } catch (error) {
      console.error('TieredOffers - Error removing cart item:', error);
      return false;
    }
  }
}

// Initialize the Tiered Offers manager
function initializeTieredOffersManager() {
  // Prevent multiple instances
  if (window.tieredOffersManager) {
    if (window.tieredOffersManager.debugMode) {
      console.log('TieredOffers - Manager already exists, skipping initialization');
    }
    return;
  }
  
  const cartItemsComponent = document.querySelector('cart-items-component');
  if (window.tieredOffersManager?.debugMode) {
    console.log('TieredOffers - Looking for cart-items-component:', cartItemsComponent);
  }
  
  if (cartItemsComponent) {
    console.log('TieredOffers - Initializing TieredOffersManager');
    window.tieredOffersManager = new TieredOffersManager();
    
    // Make manager globally accessible for remove buttons
    window.tieredOffersManager.removeItemAndUpdate = async function(element) {
      const row = element.closest('tr');
      const variantId = row.dataset.variantId;
      const isFreeItem = row.querySelector('.cart-item__free-price');
      
      if (variantId && isFreeItem) {
        // For free items, we need to find the correct line index from the actual cart
        const success = await this.removeFreeItemByVariantId(variantId);
        if (success) {
          // Remove the row from display immediately
          row.style.opacity = '0';
          row.style.transform = 'translateY(-10px)';
          row.style.transition = 'all 0.3s ease';
          setTimeout(() => {
            row.remove();
            this.updateOffersDisplay();
          }, 300);
        }
      } else {
        // For regular items, use the line index
        const lineIndex = row.dataset.lineIndex;
        if (lineIndex) {
          await this.removeCartItem(parseInt(lineIndex));
          setTimeout(() => {
            this.updateOffersDisplay();
          }, 300);
        }
      }
    };
  } else {
    console.log('TieredOffers - cart-items-component not found, retrying in 1 second');
    setTimeout(initializeTieredOffersManager, 1000);
  }
}

// Initialize when DOM is loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeTieredOffersManager);
} else {
  // DOM is already loaded
  initializeTieredOffersManager();
}

// Also initialize when cart is updated (for dynamic loading)
document.addEventListener(ThemeEvents.cartUpdate, () => {
  // Small delay to ensure DOM is updated
  setTimeout(initializeTieredOffersManager, 100);
});

// Initialize when cart drawer is opened
document.addEventListener('click', (e) => {
  const cartButton = e.target.closest('[on\\:click="/open"]');
  if (cartButton && cartButton.closest('cart-drawer-component')) {
    console.log('TieredOffers - Cart drawer opening, initializing manager');
    setTimeout(initializeTieredOffersManager, 500);
  }
});

// Also check for cart drawer periodically to ensure initialization
setInterval(() => {
  const cartDrawer = document.querySelector('cart-drawer-component');
  if (cartDrawer && !window.tieredOffersManager) {
    console.log('TieredOffers - Cart drawer found, initializing manager');
    initializeTieredOffersManager();
  }
}, 2000); // Check every 2 seconds